import { useSelector } from 'react-redux';
import { ApplicationState } from '@mews-commander/types';
import { BulkPriceDiscriminator, RateType } from '@mews-commander/data-access';
import { useFeatureFlag, NumberService } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import { createFormResolver } from '@mews-commander/form';
import { validation as validate } from '@mews-commander/legacy-form';
import { getCurrentNumberCultureInfo } from '@mews-commander/core/legacy-globalization';
import { FormErrors, WithEnterpriseIds } from '../../../../types';
import { getEnterprisesUniqDefaultLanguageCodes } from '../../../../selectors/enterprises';
import { FormValues, PreparedValues, WithDependentProp } from './types';
import { RateDialogForm } from './RateDialogForm';
import { getConstraints } from './fields/constraints';
import { keyByLanguageCodes } from './helpers';
import { FieldName, WithFieldsConfig } from './fields/types';
import {
    ABSOLUTE_ADJUSTMENT_FIELD_NAME,
    BASE_PRICE_FIELD_NAME,
    ENABLED_FIELD_NAME,
    EXTERNAL_NAME_FIELD_NAME,
    HIDE_PRICE_FROM_GUEST_FIELD_NAME,
    IS_BONUS_POINTS_ELIGIBLE_FIELD_NAME,
    NAME_FIELD_NAME,
    RATE_GROUP_KEY_FIELD_NAME,
    RATE_TYPE_FIELD_NAME,
    RELATIVE_ADJUSTMENT_FIELD_NAME,
    BUSINESS_SEGMENT_KEY_FIELD_NAME,
    RATE_KEY_FIELD_NAME,
    ACCOUNTING_CATEGORY_KEY_FIELD_NAME,
    EXTRA_OCCUPANCY_ADJUSTMENT_FIELD_NAME,
    NEGATIVE_OCCUPANCY_ADJUSTMENT_FIELD_NAME,
    BASE_RATE_KEY_FIELD_NAME,
    ORDERING_FIELD_NAME,
} from './fields';

export const DEFAULT_VALUES: FormValues = {
    [RATE_TYPE_FIELD_NAME]: RateType.Public,
    [ABSOLUTE_ADJUSTMENT_FIELD_NAME]: '',
    [BASE_RATE_KEY_FIELD_NAME]: '',
    [BASE_PRICE_FIELD_NAME]: '',
    [ENABLED_FIELD_NAME]: 1,
    [EXTERNAL_NAME_FIELD_NAME]: '',
    [NAME_FIELD_NAME]: '',
    [RATE_KEY_FIELD_NAME]: '',
    [RATE_GROUP_KEY_FIELD_NAME]: '',
    [RELATIVE_ADJUSTMENT_FIELD_NAME]: '',
    [IS_BONUS_POINTS_ELIGIBLE_FIELD_NAME]: true,
    [HIDE_PRICE_FROM_GUEST_FIELD_NAME]: false,
    [BUSINESS_SEGMENT_KEY_FIELD_NAME]: '',
    [ACCOUNTING_CATEGORY_KEY_FIELD_NAME]: '',
    [EXTRA_OCCUPANCY_ADJUSTMENT_FIELD_NAME]: '0',
    [NEGATIVE_OCCUPANCY_ADJUSTMENT_FIELD_NAME]: '0',
    [ORDERING_FIELD_NAME]: '0',
};

type RateContainerProps = Omit<React.ComponentPropsWithoutRef<typeof RateDialogForm>, 'useFormProps' | 'onSubmit'> &
    WithDependentProp &
    WithFieldsConfig &
    WithEnterpriseIds & {
        onSubmit: (preparedValues: PreparedValues, formValues: FormValues) => void;
        values?: FormValues;
        selectedRateIds?: string[];
        formErrors?: FormErrors;
        unlockField?: (fieldName: FieldName) => void;
        unlockedFields?: FieldName[];
    };

export const RateContainer = ({
    onSubmit,
    dependent,
    values,
    enterpriseIds,
    fieldsConfig,
    formErrors,
    unlockedFields,
    ...other
}: RateContainerProps) => {
    const nordicChoiceRateOptionsEnabled = useFeatureFlag(FeatureFlag.nordicChoiceRateOptionsEnabled);
    const enterprisesDefaultlanguageCodes = useSelector<ApplicationState, string[]>((state) =>
        getEnterprisesUniqDefaultLanguageCodes(state, { ids: enterpriseIds })
    );

    const resolver = createFormResolver<FormValues>(
        validate(getConstraints({ dependent, enabledFields: fieldsConfig?.enabledFields, unlockedFields }))
    );
    const numberCultureInfo = useSelector((state: ApplicationState) => getCurrentNumberCultureInfo(state));

    const formProps = {
        resolver,
        defaultValues: {
            ...DEFAULT_VALUES,
        },
        values,
    };

    const handleOnSubmit = (formValues: FormValues) => {
        const {
            absoluteValue,
            basePrice,
            extraOccupancyAdjustment,
            negativeOccupancyAdjustment,
            baseRateKey,
            description,
            externalName,
            name,
            rateGroupKey,
            relativeValue,
            shortName,
            type,
            hidePriceFromGuest,
            isBonusPointsEligible,
            rateKey,
            ordering,
            ...otherValues
        } = formValues;
        const names = keyByLanguageCodes(enterprisesDefaultlanguageCodes, name);

        const commonValues = {
            type,
            names,
            shortNames: keyByLanguageCodes(enterprisesDefaultlanguageCodes, shortName),
            descriptions: keyByLanguageCodes(enterprisesDefaultlanguageCodes, description),
            externalNames: keyByLanguageCodes(enterprisesDefaultlanguageCodes, externalName),
            rateGroupKey,
            rateKey: rateKey || undefined,
            ...(nordicChoiceRateOptionsEnabled
                ? { options: { hidePriceFromGuest, isBonusPointsEligible } }
                : undefined),
            ordering: Number(ordering),
            ...otherValues,
        };

        if (!dependent) {
            const unformattedBasePrice = NumberService.unformat(basePrice, numberCultureInfo);
            const unformattedExtraOccupancyAdjustment = NumberService.unformat(
                extraOccupancyAdjustment ?? '0',
                numberCultureInfo
            );
            const unformattedNegativeOccupancyAdjustment = NumberService.unformat(
                negativeOccupancyAdjustment ?? '0',
                numberCultureInfo
            );

            return onSubmit(
                {
                    ...commonValues,
                    price: {
                        discriminator: BulkPriceDiscriminator.BaseRate,
                        value: {
                            value: unformattedBasePrice,
                            extraOccupancyAdjustment: unformattedExtraOccupancyAdjustment,
                            negativeOccupancyAdjustment: unformattedNegativeOccupancyAdjustment,
                        },
                    },
                },
                formValues
            );
        }

        const unformattedAbsoluteValue = NumberService.unformat(absoluteValue, numberCultureInfo);
        const unformattedRelativeValue = NumberService.unformat(relativeValue, numberCultureInfo);

        return onSubmit(
            {
                ...commonValues,
                price: {
                    discriminator: BulkPriceDiscriminator.DependentRate,
                    value: {
                        baseRateKey,
                        absoluteValue: unformattedAbsoluteValue,
                        relativeValue: unformattedRelativeValue / 100,
                    },
                },
            },
            formValues
        );
    };

    return (
        <RateDialogForm
            formErrors={formErrors}
            useFormProps={formProps}
            onSubmit={handleOnSubmit}
            dependent={dependent}
            enterpriseIds={enterpriseIds}
            fieldsConfig={fieldsConfig}
            {...other}
        />
    );
};
